'use client';

import {useContext, useState} from 'react';
import styled from '@emotion/styled';

import theme from 'theme';
import {DropdownContent} from 'Dropdown';

import {SelectContext} from '../Select';
import {SelectItems} from '../SelectItems';
import {Hint} from '../Hint';
import {Loader} from '../Loader';

import {SelectState} from '../types';

import type {SelectItemsProps, SelectListProps} from '../types';

const Empty = styled.div`
  padding: 12px;
  color: ${theme.colors.grey[40]};

  ${theme.typography.p1};
`;

export const SelectList: React.FC<SelectListProps> = ({components}) => {
  const context = useContext(SelectContext);
  const {state} = context;

  const isFiltred = state === SelectState.Filtred;
  const options = isFiltred ? context.filtred : context.options;

  const [clientWidth, setClientWidth] = useState<number>();

  if (state === SelectState.Searching) {
    const child = components?.searching ?? (
      <Empty>
        <Loader>Поиск совпадений</Loader>
      </Empty>
    );

    return <DropdownContent>{child}</DropdownContent>;
  }

  if (isFiltred && !options.length) {
    const child = components?.notFound ?? <Empty>Ничего не найдено</Empty>;
    return <DropdownContent>{child}</DropdownContent>;
  }

  if (!options.length && !components?.header && !components?.footer) {
    return null;
  }

  const ref = (e: HTMLDivElement | null) => {
    setTimeout(() => {
      if (!e?.clientWidth) {
        return;
      }

      setClientWidth(e.clientWidth);
    });
  };

  const Component = components?.items ?? SelectItems;

  const selectItemsProps: SelectItemsProps = {
    state,
    options,
    clientWidth,
    name: context.name,
    value: context.value,
    onChange: context.onChange,
    multiple: context.multiple,
    setIsOpen: context.setIsOpen,
    loadMore: context.loadMore,
    height: context.height,
    maxHeight: context.maxHeight,
    virtualize: options.length > 20 ? context.virtualize : undefined,
    whiteSpace: context.whiteSpace,

    item: components?.item,
    qa: context.getQA('list'),
  };

  const child = !!clientWidth && <Component {...selectItemsProps} />;

  return (
    <DropdownContent ref={ref}>
      {components?.hint && <Hint>{components.hint}</Hint>}
      {components?.header}
      {child}
      {components?.footer}
    </DropdownContent>
  );
};
